// Type imports
import type { ColumnDef } from "@tanstack/react-table";

// Value imports
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { SelectedTrack } from "../store/albumStore";

import { FormCheckbox } from "../../../../../../newComponents/input/FormCheckbox";
import Table from "../../../../../../newComponents/table/Table";
import Text from "../../../../../../newComponents/typography/Text";
// import SelectedSongsDetails from "../SelectedSongsDetails";
import useAlbumStore from "../store/albumStore";

export default function SelectSongsStep() {
  const { t } = useTranslation("album");
  // const { albumData, tracks, isLoading, toggleTrackSelection, selectAllTracks } = useAlbumStore(); USE THIS WHEN NEEDING ALBUM DATA
  const {
    tracks,
    isLoading,
    toggleTrackSelection,
    selectAllTracks,
    toggleTrackRemix,
    toggleTrackContainsSamples,
    setAllTracksRemix,
    setAllTracksContainsSamples,
  } = useAlbumStore();

  // Format track duration from milliseconds to MM:SS
  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Define table columns
  const columns = useMemo<ColumnDef<SelectedTrack>[]>(() => [
    {
      id: "selection",
      header: ({ table }) => (
        <FormCheckbox
          defaultChecked={false}
          checked={
            table.getRowModel().rows.length > 0
            && table.getRowModel().rows.every(row => row.original.isSelected)
          }
          onChange={e => selectAllTracks(e.target.checked)}
          tableHeader={true}
        />
      ),
      cell: ({ row }) => {
        // Handle click on checkbox
        const handleCheckboxClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          toggleTrackSelection(row.original.id);
        };

        return (
          <div
            className="p-2 -m-2 cursor-pointer"
            onClick={() => toggleTrackSelection(row.original.id)}
          >
            <div onClick={handleCheckboxClick}>
              <FormCheckbox
                defaultChecked={false}
                checked={row.original.isSelected}
                onChange={() => {}} // Empty onChange handler, actual toggle happens in onClick
                className="w-4 h-4"
              />
            </div>
          </div>
        );
      },
      size: 5,
    },
    // {
    //   id: "track_number",
    //   header: "#",
    //   accessorFn: row => row.track_number,
    //   cell: ({ row }) => (
    //     <Text as="p" className="font-normal">
    //       {row.original.track_number}
    //     </Text>
    //   ),
    //   minSize: 10,
    // },
    {
      id: "title",
      header: t("album:title") ?? "Title",
      accessorFn: row => row.name,
      cell: ({ row }) => (
        <Text
          as="p"
          className="font-semibold cursor-pointer"
          onClick={() => toggleTrackSelection(row.original.id)}
        >
          {row.original.name}
        </Text>
      ),
      minSize: 500,
    },
    {
      id: "artist",
      header: t("album:artist") ?? "Artist",
      accessorFn: row => row.artists.map(artist => artist.name).join(", "),
      cell: ({ row }) => (
        <Text as="p" className="font-normal">
          {row.original.artists.map(artist => artist.name).join(", ")}
        </Text>
      ),
      minSize: 500,
    },
    {
      id: "duration",
      header: t("album:duration") ?? "Duration",
      accessorFn: row => row.duration_ms,
      cell: ({ row }) => (
        <Text as="p" className="font-normal">
          {formatDuration(row.original.duration_ms)}
        </Text>
      ),
      minSize: 50,
    },
    {
      id: "containsSamples",
      header: () => (
        <FormCheckbox
          label={t("album:contains-samples-question") ?? "Contains Samples?"}
          checked={tracks.length > 0 && tracks.every(track => track.containsSamples)}
          onChange={e => setAllTracksContainsSamples(e.target.checked)}
          className="w-4 h-4"
          smallFont={true}
          tableHeader={true}
        />
      ),
      cell: ({ row }) => {
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          toggleTrackContainsSamples(row.original.id);
        };

        return (
          <div onClick={handleClick}>
            <FormCheckbox
              checked={row.original.containsSamples || false}
              onChange={() => {}} // Empty onChange handler, actual toggle happens in onClick
              className="w-4 h-4"
            />
          </div>
        );
      },
      size: 50,
    },
    {
      id: "isRemix",
      header: () => (
        <FormCheckbox
          label={t("album:is-remix-question") ?? "Is Remix?"}
          checked={tracks.length > 0 && tracks.every(track => track.isRemix)}
          onChange={e => setAllTracksRemix(e.target.checked)}
          className="w-4 h-4"
          smallFont={true}
          tableHeader={true}
        />
      ),
      cell: ({ row }) => {
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          toggleTrackRemix(row.original.id);
        };

        return (
          <div onClick={handleClick}>
            <FormCheckbox
              checked={row.original.isRemix || false}
              onChange={() => {}} // Empty onChange handler, actual toggle happens in onClick
              className="w-4 h-4"
            />
          </div>
        );
      },
      size: 50,
    },
  ], [selectAllTracks, toggleTrackSelection, toggleTrackRemix, toggleTrackContainsSamples, setAllTracksRemix, setAllTracksContainsSamples, tracks, t]);

  // Calculate stats for the table header
  const statsBlock = useMemo(() => [
    { title: t("album:tracks") ?? "Tracks", value: tracks.length || 0, highlight: false },
    { title: t("album:selected") ?? "Selected", value: tracks.filter(t => t.isSelected).length || 0, highlight: true },
  ], [tracks, t]);

  return (

    <div className="w-full min-w-0">
      <Table
        data={tracks}
        columns={columns}
        hasFilters={false}
        noDataMessage={t("album:no-tracks-found") ?? "No tracks found"}
        isRefetchingData={isLoading}
        statsBlock={statsBlock}
        paginationComponent={false}
        pagination={{ pageIndex: 0, pageSize: tracks.length || 1000 }}

      />
    </div>
  );
}
